import React, { useEffect, useState } from 'react';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import PushPanelShell from './PushPanelShell';
import type { PushDeliveryLog, PushRecipientRow } from '../../../models/MasterAdmin/pushNotificationHub';
import {
  fetchPushRecipientDeliveries,
  fetchPushRecipients,
} from '../../../services/MasterAdmin/pushNotificationHubService';
import { getApiErrorMessage } from '../../../utils/apiErrorMessage';

function formatWhen(iso: string | null | undefined) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'medium', timeStyle: 'short' });
}

const PushRecipientsPanel: React.FC = () => {
  const [items, setItems] = useState<PushRecipientRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selected, setSelected] = useState<PushRecipientRow | null>(null);
  const [history, setHistory] = useState<PushDeliveryLog[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    if (selected) return;
    setLoading(true);
    fetchPushRecipients({ page, limit: 30, search: search || undefined })
      .then((res) => {
        setItems(res.data.items || []);
        setTotalPages(res.data.pagination?.totalPages || 1);
      })
      .catch((err: unknown) => {
        toast.error(getApiErrorMessage(err, 'Failed to load recipients'));
        setItems([]);
      })
      .finally(() => setLoading(false));
  }, [page, search, selected]);

  const openRecipient = async (row: PushRecipientRow) => {
    setSelected(row);
    setHistoryLoading(true);
    try {
      const id = row.userId || row.vendorId;
      if (!id) {
        setHistory([]);
        return;
      }
      const res = await fetchPushRecipientDeliveries(id, {
        limit: 100,
        recipientType: row.recipientType,
        vendorId: row.vendorId || undefined,
      });
      setHistory(res.data.items || []);
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to load user history'));
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  if (selected) {
    return (
      <PushPanelShell
        title={selected.name}
        description={`${selected.recipientType}${selected.phone ? ` · ${selected.phone}` : ''} · ${selected.total} push attempts`}
        actions={
          <button
            type="button"
            onClick={() => setSelected(null)}
            className="inline-flex items-center gap-1 rounded-lg border px-3 py-2 text-sm dark:border-gray-600"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            All recipients
          </button>
        }
      >
        <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
          {[
            { label: 'Sent', value: selected.sent },
            { label: 'Failed', value: selected.failed },
            { label: 'Queued', value: selected.queued },
            { label: 'Skipped', value: selected.skipped },
          ].map((card) => (
            <div key={card.label} className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900">
              <p className="text-[11px] uppercase text-gray-400">{card.label}</p>
              <p className="mt-1 text-xl font-bold">{card.value}</p>
            </div>
          ))}
        </div>
        {historyLoading ? (
          <p className="text-sm text-gray-500">Loading history…</p>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
            <table className="min-w-full divide-y text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Campaign</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">When</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {history.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-4 py-8 text-center text-gray-500">
                      No deliveries for this recipient.
                    </td>
                  </tr>
                ) : (
                  history.map((row) => (
                    <tr key={row.id}>
                      <td className="px-4 py-3">
                        <p className="font-medium">{row.campaignTitle || row.campaignId}</p>
                        {row.fcmErrorCode ? <p className="text-xs text-red-600">{row.fcmErrorCode}</p> : null}
                      </td>
                      <td className="px-4 py-3 text-xs font-medium uppercase">{row.status}</td>
                      <td className="px-4 py-3 text-xs text-gray-500">{formatWhen(row.sentAt || row.createdAt)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </PushPanelShell>
    );
  }

  return (
    <PushPanelShell
      title="Recipients"
      description="Everyone who was targeted by a push campaign. Open a row to see that user’s full delivery history."
    >
      <div className="mb-4 flex flex-wrap gap-2">
        <input
          type="search"
          placeholder="Search by name, phone, or user id…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setSearch(searchInput.trim());
              setPage(1);
            }
          }}
          className="w-full max-w-md rounded-xl border px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
        />
        <button
          type="button"
          onClick={() => {
            setSearch(searchInput.trim());
            setPage(1);
          }}
          className="rounded-lg bg-gray-900 px-3 py-2 text-sm font-medium text-white dark:bg-gray-100 dark:text-gray-900"
        >
          Search
        </button>
      </div>
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <table className="min-w-full divide-y text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Recipient</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Last campaign</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Sent</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Failed</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Last activity</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {loading ? (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-gray-500">
                  Loading…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-gray-500">
                  No recipients yet. Send a campaign to start tracking per user.
                </td>
              </tr>
            ) : (
              items.map((row) => (
                <tr
                  key={row.recipientKey}
                  className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900/40"
                  onClick={() => void openRecipient(row)}
                >
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900 dark:text-white">{row.name}</p>
                    <p className="text-xs text-gray-500">
                      {row.recipientType}
                      {row.phone ? ` · ${row.phone}` : ''}
                    </p>
                  </td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-200">{row.lastCampaignTitle || '—'}</td>
                  <td className="px-4 py-3 text-emerald-600">{row.sent}</td>
                  <td className="px-4 py-3 text-red-600">{row.failed}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{formatWhen(row.lastAt)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 1 ? (
        <div className="mt-3 flex justify-end gap-2">
          <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-lg border px-3 py-1.5 text-sm disabled:opacity-40">
            Previous
          </button>
          <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="rounded-lg border px-3 py-1.5 text-sm disabled:opacity-40">
            Next
          </button>
        </div>
      ) : null}
    </PushPanelShell>
  );
};

export default PushRecipientsPanel;
